import React, { useContext, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import flocknoteStateHooks from "Flocknote/stateHooks";

import API from "Boards/API";
import { BoardContext } from "Boards/BoardContext";
import { TitleCellWrapper } from "Boards/Cells";
import { BOARD_TYPES, COLUMN_TYPES } from "Boards/const";
import useGroupOrSignupID from "Boards/useGroupOrSignupID";

import FnrButton from "UI/FnrButton3";
import FnrInput from "UI/FnrInput";
import { FnrPopover } from "UI/FnrPopover2";
import {
  FnrToggle,
  FnrToggleChild,
  FnrToggleItems,
  FnrToggleTitle,
} from "UI/FnrToggle2";
import { colors } from "UI/colors";

import AddColumnShared from "./AddColumnShared";
import ProfileFieldsAndSacraments from "./ProfileFieldsAndSacraments";

export default function AddColumn({ orderBy, isAfterColumn }) {
  const [{ boardID, boardType }, dispatch] = useContext(BoardContext);
  const [{ hasSignupsProfileFields }] =
    flocknoteStateHooks.useNetworkMetadata();

  const groupOrSignupID = useGroupOrSignupID();

  const [showPopover, setShowPopover] = useState(false);
  const [newColumnLoading, setNewColumnLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(INITIAL_TAB);
  const [groups, setGroups] = useState(null);
  const [existingColumnReadyToSave, setExistingColumnReadyToSave] =
    useState(false);
  const [columnName, setColumnName] = useState("");
  const plusButtonRef = useRef();
  const [columnType, setColumnType] = useState(INITIAL_SELECTED_COLUMN_TYPE);
  const [triggerResize, setTriggerResize] = useState(0);
  const [selectedSacrament, setSelectedSacrament] = useState();

  // Shared Column State
  const [selectedGroupID, setSelectedGroupID] = useState(null);
  const [selectedBoardID, setSelectedBoardID] = useState(null);
  const [selectedColumnID, setSelectedColumnID] = useState(null);

  const handleClick = () => {
    if (showPopover) onShouldClosePopover();
    else setShowPopover(true);
  };

  const onShouldClosePopover = () => {
    if (!newColumnLoading) setShowPopover(false);
  };

  useEffect(() => {
    if (!showPopover) {
      setTimeout(() => {
        setColumnType(INITIAL_SELECTED_COLUMN_TYPE);
        setColumnName("");
        setNewColumnLoading(false);
        setCurrentTab(INITIAL_TAB);
      }, 250);
    }
  }, [showPopover]);

  const updateColumns = (columns) => {
    dispatch({ type: "Update Columns", payload: columns });
    setShowPopover(false);
  };

  const createColumn = () => {
    if (currentTab === TABS.EXISTING && existingColumnReadyToSave)
      createSharedColumn();
    else createNewColumn();
  };

  const createNewColumn = () => {
    setNewColumnLoading(true);
    API.addColumn(boardID, {
      orderBy,
      title: columnName,
      columnType,
      ...groupOrSignupID,
    })
      .post()
      .then(({ columns }) => {
        updateColumns(columns);
      })
      .catch((err) => {
        console.log("err:", err);
      });
  };

  const createSharedColumn = () => {
    setNewColumnLoading(true);
    API.linkColumnToBoard(boardID, {
      linkColumnID: selectedColumnID,
      order: orderBy,
      ...groupOrSignupID,
    })
      .post()
      .then(({ columns }) => {
        // Refresh to update board data
        window.location = window.location.pathname;
      });
  };

  const onAnimationComplete = () => {
    setTriggerResize((current) => current + 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createColumn();
  };

  const selectExistingPanel = () => {
    if (groups) {
      setCurrentTab(TABS.EXISTING);
      return;
    }

    API.getAvailableColumnsToLinkToBoard(boardID)
      .post()
      .then((resp) => {
        setGroups(resp?.groups || null);
        setCurrentTab(TABS.EXISTING);
      })
      .catch((err) => console.log("something has gone awry.", err));
  };

  const selectExtendedFieldPanel = () => {
    setCurrentTab(TABS.EXTENDED_FIELD);
  };

  const toggleStyle = {
    background: "initial",
    border: "none",
    borderRadius: "initial",
    paddingBottom: "6px",
    borderBottom: `1px solid ${colors.fnGray400}`,
    marginBottom: "6px",
  };

  const toggleHoverStyle = {
    backgroundColor: `${colors.fnScrollbarDarkGray}`,
  };

  const toggleChildStyle = {
    color: "white",
  };

  const onlyAllowSimpleColumns = boardType === BOARD_TYPES.SIGNUP;
  const currentTabIsExtendedField =
    currentTab === TABS.EXTENDED_FIELD ||
    currentTab === TABS.SACRAMENTS ||
    currentTab === TABS.SACRAMENT_FIELDS;

  return (
    <>
      <PlusButton
        data-cy="addColumnToBoardButton"
        ref={plusButtonRef}
        onClick={handleClick}
        className={showPopover ? "popover_open" : null}
        isAfterColumn={isAfterColumn}
      >
        <div className="fas fa-plus-circle" />
      </PlusButton>
      <FnrPopover
        buttonRef={plusButtonRef}
        show={showPopover}
        onShouldClose={onShouldClosePopover}
        onAnimationComplete={onAnimationComplete}
        dataElementToIgnore="fnr-popover-option"
      >
        <CreateColumnPopover>
          <Title>
            <TitleIcon className="far fa-columns" /> Add a Column
          </Title>

          {!onlyAllowSimpleColumns ? (
            <FnrToggle
              style={toggleStyle}
              hoverStyle={toggleHoverStyle}
              triggerResize={triggerResize}
            >
              <FnrToggleItems>
                <FnrToggleChild
                  selected={currentTab === TABS.NEW}
                  style={toggleChildStyle}
                >
                  <UseSharedToggleButton
                    onClick={() => setCurrentTab(TABS.NEW)}
                  >
                    New
                  </UseSharedToggleButton>
                </FnrToggleChild>
                <FnrToggleChild
                  selected={currentTab === TABS.EXISTING}
                  style={toggleChildStyle}
                >
                  <UseSharedToggleButton onClick={selectExistingPanel}>
                    Existing
                  </UseSharedToggleButton>
                </FnrToggleChild>
                {hasSignupsProfileFields && (
                  <FnrToggleChild
                    selected={currentTabIsExtendedField}
                    style={toggleChildStyle}
                  >
                    <UseSharedToggleButton onClick={selectExtendedFieldPanel}>
                      Profile Field
                    </UseSharedToggleButton>
                  </FnrToggleChild>
                )}
              </FnrToggleItems>
            </FnrToggle>
          ) : null}

          {currentTab === TABS.NEW && (
            <>
              <form onSubmit={handleSubmit}>
                <FnrInput
                  data-cy="addAColumnModalTitleInput"
                  value={columnName}
                  onChange={setColumnName}
                  placeholder="Column Title"
                />
              </form>
              <FnrToggle
                style={{ marginTop: 12 }}
                triggerResize={triggerResize}
              >
                <FnrToggleTitle>Column Type</FnrToggleTitle>
                <FnrToggleItems>
                  <FnrToggleChild selected={columnType === COLUMN_TYPES.BOOL}>
                    <ColumnTypeButton
                      data-cy="addAColumnModalChecklistType"
                      onClick={() => setColumnType(COLUMN_TYPES.BOOL)}
                    >
                      <ColumnTypeIcon className="far fa-check-circle" />
                      Checklist
                    </ColumnTypeButton>
                  </FnrToggleChild>
                  <FnrToggleChild selected={columnType === COLUMN_TYPES.TEXT}>
                    <ColumnTypeButton
                      data-cy="addAColumnModalTextType"
                      onClick={() => setColumnType(COLUMN_TYPES.TEXT)}
                    >
                      <ColumnTypeIcon className="far fa-text" />
                      Text
                    </ColumnTypeButton>
                  </FnrToggleChild>
                  <FnrToggleChild selected={columnType === COLUMN_TYPES.NUMBER}>
                    <ColumnTypeButton
                      data-cy="addAColumnModalNumberType"
                      onClick={() => setColumnType(COLUMN_TYPES.NUMBER)}
                    >
                      <ColumnTypeIcon className="far fa-calculator" />
                      Number
                    </ColumnTypeButton>
                  </FnrToggleChild>
                </FnrToggleItems>
              </FnrToggle>
            </>
          )}
          {currentTab === TABS.EXISTING && (
            <AddColumnShared
              groups={groups}
              setReadyToSave={setExistingColumnReadyToSave}
              selectedGroupID={selectedGroupID}
              selectedBoardID={selectedBoardID}
              selectedColumnID={selectedColumnID}
              setSelectedGroupID={setSelectedGroupID}
              setSelectedBoardID={setSelectedBoardID}
              setSelectedColumnID={setSelectedColumnID}
            />
          )}
          {currentTab === TABS.EXTENDED_FIELD && (
            <ProfileFieldsAndSacraments
              orderBy={orderBy}
              newColumnLoading={newColumnLoading}
              setNewColumnLoading={setNewColumnLoading}
              setShowPopover={setShowPopover}
              setCurrentTab={setCurrentTab}
              setSelectedSacrament={setSelectedSacrament}
              boardID={boardID}
              selectedSacrament={selectedSacrament}
            />
          )}
          {!currentTabIsExtendedField && (
            <ButtonRow>
              <FnrButton
                disabled={newColumnLoading}
                data-cy="addAColumnModalCancelButton"
                onClick={onShouldClosePopover}
              >
                Cancel
              </FnrButton>
              <FnrButton
                loading={newColumnLoading}
                data-cy="addAColumnModalSaveButton"
                onClick={createColumn}
                color="blue"
                disabled={
                  currentTab === TABS.EXISTING && !existingColumnReadyToSave
                }
              >
                Save
              </FnrButton>
            </ButtonRow>
          )}
        </CreateColumnPopover>
      </FnrPopover>
    </>
  );
}

export const TABS = Object.freeze({
  NEW: 1,
  EXISTING: 2,
  EXTENDED_FIELD: 3,
});
const INITIAL_TAB = TABS.NEW;

const INITIAL_SELECTED_COLUMN_TYPE = COLUMN_TYPES.BOOL;

const PlusButton = styled.div`
  position: absolute;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #828c8d;
  left: ${({ isAfterColumn }) => (isAfterColumn ? null : "0")};
  right: ${({ isAfterColumn }) => (isAfterColumn ? "-9px" : null)};
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  margin-left: -9px;
  background: #fff;
  border-radius: 20px;
  transition-property: opacity, visibility, transform;
  transition-duration: 0.2s, 0.2s, 0.2s;
  transition-delay: 0.1s, 0.1s, 0s;

  ${TitleCellWrapper}:hover & {
    opacity: 1;
    visibility: visible;
    transition-delay: 0s, 0s, 0s;
  }

  &:active {
    transform: scale(0.8);
  }
`;

const CreateColumnPopover = styled.div`
  padding: 8px;
  width: 280px;
  box-sizing: border-box;

  .HoverBackground {
    background: rgba(255, 255, 255, 0.4);
  }
`;

const UseSharedToggleButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 6px;
  box-sizing: border-box;
`;

const ColumnTypeButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 6px;
  box-sizing: border-box;
  width: 86px;
`;
const ColumnTypeIcon = styled.div`
  font-size: 26px;
  margin-bottom: 2px;
  padding-top: 2px;
`;

const ButtonRow = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
`;

const Title = styled.div`
  font-size: 24px;
  margin-bottom: 12px;
`;
const TitleIcon = styled.div`
  margin-right: 6px;
  margin-left: 4px;
`;
